import {
  Button,
  Divider,
  Grid,
  Heading,
  Icon,
  IconNames,
  Link,
  Message,
  Text,
  useIsDesignLanguage,
  DesignLanguage,
  useDesignLanguagePerClass,
  useModal,
} from '@afterpaytouch/core'
import { PortalApiError } from '@afterpaytouch/portal-api'
import clsx from 'clsx'
import React, { FunctionComponent, ReactNode } from 'react'
import { Money } from '../'
import { Trans } from '../../utils/convergence'
import { app } from '../../env'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { APPLE_WALLET_PAYMENT_OFFER_RESULT_URL } from '../../flow/apple-paylater/constants'
import { CheckoutError, checkoutErrors, IdentityVerificationTerminalErrorModalKey, isTerminalErrorCode, TerminalErrorCode } from '../../model'
import {
  useApplePayEntryData,
  useCheckoutState,
  useCLRiskDeclineError,
  useConsumerLendingEligible,
  useCountry,
  useIsAfterpayAppGiftCardFlow,
  useIsAfterpayFinanceFeeEligible,
  useIsApplePaylater,
  useIsOrderAmountExceedsSpendCap,
  useIsRecoverableFlow,
  useMerchantName,
  useRedirectUrl,
  useUkRegFlags,
} from '../../state'
import styles from './styles.module.css'
import { CloseCheckout } from '../CloseCheckout'
import { TERMINAL_ERROR_CODE_IMAGE_MAP, getLinkAmpEvent, isAlternativeOptionTypeArray, shouldHideDescription } from './helper'
import { useCloseCheckout } from '../../utils/close'
import { IdentityVerificationTerminalErrorModal } from '../IdentityVerificationTerminalErrorModal'
import { useTranslation } from '../../utils/convergence'
import { isNullOrUndefined, valueIsNotNullOrUndefined } from '@afterpay/utils'
import { Illustration } from '../Illustration'

interface AlternativeTitleProps {
  children?: ReactNode
  showInArcade?: boolean
}

export const AlternativeTitle: FunctionComponent<AlternativeTitleProps> = ({ children, showInArcade = false }): ReactNode => {
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  if (isArcadeDesignLanguage && !showInArcade) {
    return null
  }

  return (
    <div className={styles.alternativeTitle}>
      <Heading size='L'>{children}</Heading>
    </div>
  )
}

export const getAltTitleContext = (errorKey: string, isRecoverableFlow: boolean, isConsumerLendingEligible: boolean): string => {
  const isDeclinePCL = errorKey === TerminalErrorCode.InvalidAmount
  const isStateRestrictions = errorKey === TerminalErrorCode.StateRestrictionsPCL
  const isCreditFreezeDeclinedPCL = errorKey === TerminalErrorCode.CreditFreezeDeclinedPCL
  const isComplianceDeclineBureau = errorKey === TerminalErrorCode.ComplianceDeclineBureau
  const isKycRefreshFlow = errorKey === TerminalErrorCode.KYCRefreshPending
  switch (true) {
    case isConsumerLendingEligible || isDeclinePCL || isStateRestrictions || isCreditFreezeDeclinedPCL:
      return 'declinePCL'
    case isComplianceDeclineBureau:
      return 'complianceDeclineBureau'
    case isRecoverableFlow:
      return 'recoverable'
    case isKycRefreshFlow:
      return 'KYCRefreshPending'
    default:
      return ''
  }
}
export const TerminalError: FunctionComponent<CheckoutError> = ({ error, errorCode, extra, showBackToStore = true }) => {
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const { t } = useTranslation()
  const PORTAL_URL: string = app.PORTAL_URL
  const { checkout, started } = useCheckoutState()
  const { error: clError } = useCLRiskDeclineError()
  const country = useCountry()
  const isAfterpayAppGiftCardFlow = useIsAfterpayAppGiftCardFlow()
  const showClose = showBackToStore && checkout !== undefined
  let errorKey = isTerminalErrorCode(error) ? error : checkoutErrors[clError?.errorCode as string] ?? TerminalErrorCode.Default
  const installmentsCount = started?.paymentSchedules?.length ?? 4
  const merchantName = useMerchantName()
  const { isOrderAmountExceedsSpendCap, isOrderAmountExceedsSpendCapAndNotEnabled } = useIsOrderAmountExceedsSpendCap(errorKey)
  if (isOrderAmountExceedsSpendCapAndNotEnabled) {
    errorKey = TerminalErrorCode.Default
  }
  const errorImage = TERMINAL_ERROR_CODE_IMAGE_MAP[errorKey]
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const designLanguageClass = useDesignLanguagePerClass(styles)
  const isRecoverableFlow = useIsRecoverableFlow()
  const isConsumerLendingEligible = useConsumerLendingEligible()
  const { isUkRegulationEnabled } = useUkRegFlags()
  const isDeclinePCL = isConsumerLendingEligible || errorKey === TerminalErrorCode.InvalidAmount
  const isUkRegulatedOverdueError =
    isUkRegulationEnabled && (errorKey === TerminalErrorCode.OverduePayments || errorKey === TerminalErrorCode.OverduePaymentsCheckoutPaymentEligible)
  const showUkRegulatedOverdueSectionHeadings = isArcadeDesignLanguage && isUkRegulatedOverdueError

  const altTitleContext = getAltTitleContext(errorKey, isRecoverableFlow, isConsumerLendingEligible)
  const failureCallbackUrl = checkout?.merchant?.failureCallbackUrl
  const close = useCloseCheckout({ redirectUrlOverride: failureCallbackUrl })
  const testNameSpace = errorCode ?? clError?.errorCode ?? errorKey
  const isAmountTooHigh = errorKey === TerminalErrorCode.AmountTooHighDeclinePCL || errorKey === TerminalErrorCode.AmountTooHigh

  const headingIconExclusionErrorCode = [TerminalErrorCode.Default]
  const isHeadingIconExcluded = headingIconExclusionErrorCode.includes(errorKey)
  const showMintBar = !isOrderAmountExceedsSpendCap
  const isAFF = useIsAfterpayFinanceFeeEligible()
  const applePayEntryData = useApplePayEntryData()
  const redirectUrl = useRedirectUrl()
  const isApplePaylater = useIsApplePaylater()

  const { showModal: showInformationModal } = useModal(IdentityVerificationTerminalErrorModalKey)

  const renderLimits = (): ReactNode => {
    if (isNullOrUndefined(extra?.availableToSpend)) {
      return null
    }

    // No cash theme for OrderAmountExceedsSpendCap error
    if (isOrderAmountExceedsSpendCapAndNotEnabled) {
      return null
    }

    const limitMoney = extra?.availableToSpend

    if (isArcadeDesignLanguage) {
      return (
        <div className={designLanguageClass.limitsWrapper} data-testid='terminal-error-limits'>
          <Grid verticalGutter={{ sm: 1, md: 1, lg: 1 }}>
            <Grid.Item sm={12}>
              <div className={designLanguageClass.limitsRowItem}>
                <Text>{t('terminalError:limits.orderTotal')}</Text>
                <Money size={'M'} value={checkout?.amount} />
              </div>
            </Grid.Item>
            <Grid.Item sm={12}>
              <div className={designLanguageClass.limitsRowItem}>
                <Text>
                  {isUkRegulationEnabled ? t('terminalError:limits.availableToSpend', { context: 'regulated' }) : t('terminalError:limits.availableToSpend')}
                </Text>
                <Money size={'M'} value={limitMoney} />
              </div>
            </Grid.Item>
          </Grid>
        </div>
      )
    }

    return (
      <div className={styles.availableToSpendWrapper} data-testid='terminal-error'>
        <Money mintBar={showMintBar} value={limitMoney} size='XXXXL' bold />
        <Text bold={isOrderAmountExceedsSpendCap}>
          {isUkRegulationEnabled ? t('terminalError:availableToSpend', { context: 'regulated' }) : t('terminalError:availableToSpend')}
        </Text>
        <Divider kind='Hairline' />
      </div>
    )
  }

  const renderHeadingIcon = (): ReactNode => {
    if (!isArcadeDesignLanguage || isHeadingIconExcluded || showUkRegulatedOverdueSectionHeadings) {
      return null
    }
    return (
      <div className={styles.headingIconWrapper} data-testid='terminal-error-heading-icon'>
        <Icon name='AlertFill' color='Fire' size='Large' />
      </div>
    )
  }

  const geti18nDescContext = (): string => {
    const recoverableFlowContext = isRecoverableFlow ? 'recoverable' : ''

    if (errorKey === TerminalErrorCode.AmountTooHighDeclinePCL && typeof extra?.maximumOrderValue === 'undefined') {
      return 'generic'
    }

    if ((errorKey === TerminalErrorCode.AffordabilityDecline || errorKey === TerminalErrorCode.CreditCheckFailure) && extra?.isInitialLimit === false) {
      return 'existingCustomer'
    }

    if (extra?.payMonthlyOnly !== undefined && extra?.payMonthlyOnly) {
      return 'payMonthlyOnly'
    }

    if (isUkRegulatedOverdueError) {
      // Remove this regulated context once `rocketship.checkout-enable-uk-regulation` fully rolls out the UK overdue decline copy.
      return 'regulated'
    }

    if (isUkRegulationEnabled && isRecoverableFlow && errorKey === TerminalErrorCode.AmountTooHigh) {
      return 'recoverableRegulated'
    }

    return recoverableFlowContext
  }

  const i18nDescription = `terminalError:${errorKey}.description`

  const renderDescription = (): JSX.Element | null => {
    const context = geti18nDescContext()

    const description = (
      <Trans
        i18nKey={i18nDescription}
        values={{ ...extra, maximumOrderValue: extra?.maximumOrderValue, numberOrdersToPay: extra?.numberOrdersToPay, interval: installmentsCount }}
        components={{
          alternativeTitle: <AlternativeTitle />,
          contact: <Link key='description-link' target='_blank' kind='Underline' href={t('common:contactUrl')} />,
          minimumOrderValue: <Money size='M' value={extra?.minimumOrderValue} />,
          bold: <strong />,
          br: <br />,
          helpLandingUrl: <Link key='description-link' target='_blank' kind='Underline' href={t('common:helpLandingUrl')} />,
        }}
        context={context}
        defaults={' '}
      />
    )

    if (isArcadeDesignLanguage) {
      return (
        <Text renderAs='div' testNameSpace={errorCode} color={showUkRegulatedOverdueSectionHeadings ? 'Subtle' : undefined}>
          {description}
        </Text>
      )
    }

    const hideDescription = shouldHideDescription({ isAmountTooHigh, context, errorKey, isOrderAmountExceedsSpendCap, country })
    if (hideDescription) {
      return null
    }

    return (
      <Text renderAs='div' testNameSpace={testNameSpace}>
        {description}
      </Text>
    )
  }

  /* eslint-disable @afterpay/i18n-only-static-keys, @afterpay/i18n-no-dynamic-context */
  const renderDescriptionHeading = (): ReactNode => {
    if (!showUkRegulatedOverdueSectionHeadings) {
      return null
    }

    return <AlternativeTitle showInArcade>{t(`terminalError:${errorKey}.descriptionHeading`, { context: geti18nDescContext() })}</AlternativeTitle>
  }

  const renderAlternativeOptions = (): JSX.Element | null => {
    const descContext = geti18nDescContext()
    const optionContext = descContext === 'recoverableRegulated' ? 'recoverable' : descContext
    const option = t(`terminalError:${errorKey}.option`, {
      returnObjects: true,
      defaultValue: [],
      context: optionContext,
    })
    if (!isAlternativeOptionTypeArray(option)) {
      return null
    }

    const maximumOrderValue = extra?.maximumOrderValue ?? (clError as PortalApiError)?.errorInfo?.maximum_order_value

    const mkIcon = (index: number, icon?: IconNames): JSX.Element => (
      <>
        {icon !== undefined && (
          <div className={styles.iconWrapper} key={`alternative-option-icon-${index}`}>
            <Icon name={icon} size={showUkRegulatedOverdueSectionHeadings ? 'Medium' : 'Small'} />
          </div>
        )}
      </>
    )

    const mkLink = (index: number, url?: string): JSX.Element => {
      let validUrl = url
      if (isOrderAmountExceedsSpendCap) {
        validUrl = PORTAL_URL
      }
      const { event, options } = getLinkAmpEvent(errorKey, validUrl)

      return validUrl !== undefined ? (
        <Link
          key={`alternative-option-link-${index}`}
          target='_blank'
          kind='Underline'
          color={showUkRegulatedOverdueSectionHeadings ? 'Standard' : undefined}
          href={validUrl}
          onClick={() => logEvent(event, options)}
        />
      ) : (
        <></>
      )
    }

    const mkCloseCheckout = (index: number): JSX.Element => {
      return <Link key={`alternative-option-link-${index}`} kind='Underline' onClick={() => void close()} />
    }

    const alternativeTitleKey = isOrderAmountExceedsSpendCap ? 'alternativeTitle_recoverable' : 'alternativeTitle'
    const alternativeTitle = showUkRegulatedOverdueSectionHeadings
      ? t(`terminalError:${errorKey}.optionHeading`, { context: geti18nDescContext() })
      : t(`terminalError:${alternativeTitleKey}`, { context: altTitleContext })

    return (
      <>
        <AlternativeTitle showInArcade={showUkRegulatedOverdueSectionHeadings}>{alternativeTitle}</AlternativeTitle>
        <ul className={styles.alternativeOptionList}>
          {option.map(({ title, url, icon }, index) => (
            <li
              key={`alternative-option-${index}`}
              className={clsx(
                styles.alternativeOption,
                icon !== undefined && styles.alternativeOptionWithIcon,
                showUkRegulatedOverdueSectionHeadings && icon !== undefined && styles.regulatedOverdueOptionWithIcon
              )}
            >
              {mkIcon(index, icon)}
              <Text renderAs='div' testNameSpace={`alt-option-${index}`} color={showUkRegulatedOverdueSectionHeadings ? 'Subtle' : undefined}>
                <Trans
                  key={`terminal-error-alternative-option-${index}`}
                  i18nKey={title}
                  values={{ interval: installmentsCount, merchantName, maximumOrderValue }}
                  components={{ a: mkLink(index, url), close: mkCloseCheckout(index) }}
                />
              </Text>
            </li>
          ))}
        </ul>
      </>
    )
  }

  const renderMoreInformation = (): ReactNode => {
    if (errorKey === TerminalErrorCode.IdentityVerificationDeclinedPCL) {
      return (
        <>
          <IdentityVerificationTerminalErrorModal />
          <Message
            kind='Basic'
            iconName='Info'
            heading={t(`terminalError:identityVerificationDeclinedPCL.information.heading`)}
            description={t(`terminalError:identityVerificationDeclinedPCL.information.message`)}
            link={
              <div className={styles.informationTextContainer}>
                <Link size='XS' bold kind='Underline' onClick={() => showInformationModal()}>
                  {t(`terminalError:identityVerificationDeclinedPCL.information.link`)}
                </Link>
              </div>
            }
          />
        </>
      )
    }
    if (errorKey === TerminalErrorCode.KYCRefreshPending) {
      return <Text>{t(`terminalError:${errorKey}.detail`)}</Text>
    }
    return null
  }

  const renderOrderAmountExceedsSpendCapAlternativeOptions = (): ReactNode => (
    <>
      <AlternativeTitle>{t('terminalError:alternativeTitle_meaning', { context: altTitleContext })}</AlternativeTitle>
      <Text renderAs='div'>
        <Trans i18nKey={t(`terminalError:${errorKey}:meaning_description`, { context: altTitleContext })} />
      </Text>
    </>
  )

  const renderSubheading = (): ReactNode => {
    if (isDeclinePCL && isAmountTooHigh && extra?.maximumOrderValue) {
      const maximumOrderValue = extra?.maximumOrderValue.split('.')[0]
      return (
        <>
          <div className={styles.underlineContainer}>
            <Text size='XXXXL' bold>
              {maximumOrderValue}
            </Text>
            <div className={designLanguageClass.underline}></div>
          </div>
          <div className={styles.textContainer}>
            <Text testNameSpace={`${testNameSpace}-subheading`}>{t(`terminalError:${errorKey}.subheading`)}</Text>
          </div>
          <Divider kind='Hairline' />
        </>
      )
    }
    return null
  }

  const showHelpCenterButton = errorKey === TerminalErrorCode.IdentityVerificationFailedManual

  const helpCenterButton = (): ReactNode => {
    const openUrl = (): void => {
      const url = t('common:identityHelpUrl')
      window.open(url, '_self', 'noopener')
    }

    return (
      <Button kind='Secondary' padding='Fluid' onClick={openUrl} testNameSpace={testNameSpace}>
        {t(`terminalError:${errorKey}.cta`)}
      </Button>
    )
  }
  /* eslint-enable @afterpay/i18n-only-static-keys, @afterpay/i18n-no-dynamic-context */

  const renderCta = (): ReactNode => {
    if (isAfterpayAppGiftCardFlow || isAFF) {
      return null
    }

    return (
      <CloseCheckout kind='Secondary' padding='Fluid' testNameSpace='terminal-error' redirectUrlOverride={failureCallbackUrl}>
        {t('terminalError:backToStore')}
      </CloseCheckout>
    )
  }

  const renderApplePaylaterCta = (): ReactNode => {
    const handleDone = (): void => {
      if (redirectUrl) {
        window.location.href = redirectUrl
      } else if (valueIsNotNullOrUndefined(applePayEntryData?.sessionIdentifier)) {
        window.location.href = `${APPLE_WALLET_PAYMENT_OFFER_RESULT_URL}?sessionIdentifier=${applePayEntryData.sessionIdentifier}`
      }
    }

    return (
      <Button kind='Secondary' padding='Fluid' onClick={handleDone} testNameSpace='terminal-error-done'>
        {t('terminalError:Done')}
      </Button>
    )
  }

  return (
    <>
      <Grid>
        <div>
          {errorImage && (
            <div className={styles.heroImage}>
              <Illustration name={errorImage} width={327} height={250} testId='terminal-error-hero-image' />
            </div>
          )}
          {renderHeadingIcon()}
          <Heading size='XXL' testNameSpace={testNameSpace}>
            <Trans
              i18nKey={[`terminalError:${errorKey}.heading`, 'terminalError:default.heading']}
              context={geti18nDescContext()}
              values={{ maximumOrderValue: extra?.maximumOrderValue }}
              components={{
                minimumOrderValue: <Money size='XXL' bold value={extra?.minimumOrderValue} />,
              }}
            />
          </Heading>
          {renderLimits()}
          {renderSubheading()}
          <div data-testid='terminal-error-description-wrapper' className={designLanguageClass.descriptionWrapper}>
            {renderDescriptionHeading()}
            {renderDescription()}
            {isOrderAmountExceedsSpendCap && renderOrderAmountExceedsSpendCapAlternativeOptions()}
            {renderAlternativeOptions()}
            {renderMoreInformation()}
          </div>
        </div>
      </Grid>
      {isApplePaylater && (
        <Grid>
          <Grid.Item>{renderApplePaylaterCta()}</Grid.Item>
        </Grid>
      )}
      {!isApplePaylater && showClose && (
        <Grid>
          <Grid.Item>{showHelpCenterButton ? helpCenterButton() : renderCta()}</Grid.Item>
        </Grid>
      )}
    </>
  )
}
